// src/services/investorService.js (Consolidated)
import api from '@/core/api/api';
import { ROUTES } from '@/core/constants/routes';

/**
 * Original FilterGenie Lambda (`ai_filter_parse.py`) validates and returns camelCase keys:
 * assetTypes, strategyFocus, dealSizeMin, dealSizeMax, minReturnMin, daysRemainingMax.
 * Explore V2 state and `dev_get_opportunities_v2` expect snake_case: asset_types, strategy_focus,
 * deal_size_min, deal_size_max, min_return, days_remaining_max.
 * When the V2 API route points at the original parser, map here so chips + search stay in sync.
 * If the response already uses snake_case (V2-only lambda), this is a no-op aside from stripping stray camelCase.
 */
function normalizeExploreGenieFilters(filters = {}) {
  if (!filters || typeof filters !== 'object') return {};
  const out = { ...filters };
  if (!out.asset_types?.length && out.assetTypes?.length) {
    out.asset_types = [...out.assetTypes];
  }
  if (!out.strategy_focus?.length && out.strategyFocus?.length) {
    out.strategy_focus = [...out.strategyFocus];
  }
  if (out.deal_size_min == null && out.dealSizeMin != null) {
    out.deal_size_min = out.dealSizeMin;
  }
  if (out.deal_size_max == null && out.dealSizeMax != null) {
    out.deal_size_max = out.dealSizeMax;
  }
  if (out.min_return == null && out.minReturnMin != null) {
    out.min_return = out.minReturnMin;
  }
  if (out.days_remaining_max == null && out.daysRemainingMax != null) {
    out.days_remaining_max = out.daysRemainingMax;
  }
  delete out.assetTypes;
  delete out.strategyFocus;
  delete out.dealSizeMin;
  delete out.dealSizeMax;
  delete out.minReturnMin;
  delete out.daysRemainingMax;
  return out;
}

/**
 * Consolidated investor service that handles all investor-related API calls
 */
const investorService = {
  // ===== INVESTOR EXPLORATION =====
  
  /**
   * Get investors with filtering and pagination
   * @param {Object} params - Search and filter parameters
   */
  getInvestors: async (params = {}) => {
    try {
      const response = await api.get('/fetch/investors', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching investors:', error);
      // Return mock data if API fails for development purposes
      return {
        investors: [
          {
            user_id: 'user_123',
            first_name: 'John',
            last_name: 'Doe',
            email: 'john.doe@example.com',
            investor_phone: '+1-555-0123',
            investor_status: 'active',
            investor_type: 'institutional',
            investor_country: 'USA',
            created_at: '2024-01-15T10:30:00Z',
            institution_id: 'inst_456',
            institution_name: 'ABC Capital Partners',
            institution_type: 'hedge_fund',
            institution_founded_year: 2015,
            institution_website: 'https://abc-capital.com',
            institution_description: 'Multi-strategy hedge fund focused on global opportunities',
            institution_aum: '500000000',
            institution_typical_investment_size: '10000000',
            institution_address: '123 Wall Street, Suite 4500',
            institution_city: 'New York',
            institution_state: 'NY',
            institution_postal_code: '10005',
            institution_country: 'USA',
            institution_contacts: 'john.doe@abc-capital.com, +1-555-0123',
            institution_primary_contact_id: 'contact_789',
            institution_communication_preference: 'email',
            institution_decision_process: 'committee',
            institution_title: 'Managing Partner'
          },
          {
            user_id: 'user_124',
            first_name: 'Jane',
            last_name: 'Smith',
            email: 'jane.smith@example.com',
            investor_phone: '+1-555-0124',
            investor_status: 'active',
            investor_type: 'pension_fund',
            investor_country: 'Canada',
            created_at: '2024-01-16T11:30:00Z',
            institution_id: 'inst_457',
            institution_name: 'Maple Leaf Pension Fund',
            institution_type: 'pension_fund',
            institution_founded_year: 1990,
            institution_website: 'https://mapleleafpension.ca',
            institution_description: 'Leading Canadian pension fund with global investment focus',
            institution_aum: '75000000000',
            institution_typical_investment_size: '50000000',
            institution_address: '456 Bay Street, Suite 2000',
            institution_city: 'Toronto',
            institution_state: 'ON',
            institution_postal_code: 'M5H 2N2',
            institution_country: 'Canada',
            institution_contacts: 'jane.smith@mapleleafpension.ca, +1-555-0124',
            institution_primary_contact_id: 'contact_790',
            institution_communication_preference: 'email',
            institution_decision_process: 'board',
            institution_title: 'Investment Director'
          }
        ],
        pagination: {
          pageSize: 10,
          total: 2,
          nextKey: null,
          hasMore: false
        }
      };
    }
  },

  /**
   * Format investor data for display in the UI
   * @param {Object} investor - Raw investor data from API
   */
  formatInvestorForDisplay: (investor) => {
    return {
      id: investor.id || investor.investor_id,
      name: investor.name || investor.investor_name || 'Unnamed Investor',
      institutionName: investor.institution_name || investor.institutionName || investor.firm_name,
      type: investor.type || investor.investor_type || 'individual',
      country: investor.country || investor.location || investor.region,
      investmentSize: investor.investment_size || investor.investmentSize || investor.deal_size_range,
      aum: investor.aum || investor.assets_under_management || investor.total_assets,
      interests: investor.interests || investor.investment_interests || investor.strategy_focus || [],
      status: investor.status || investor.investor_status || 'Active',
      experience: investor.experience || investor.experience_level || 'Intermediate',
      experienceLevel: investor.experience_level_percentage || investor.experience_percentage || 50
    };
  },

  // ===== DASHBOARD =====
  
  /**
   * Get investor dashboard data with participating opportunities, metrics, and timeframe data
   * @param {string} userId - Investor user ID
   * @param {number} page - Page number for pagination (default: 1)
   * @param {number} pageSize - Number of items per page (default: 12)
   * @param {number} daysBack - Days to look back for recent launches (default: 7)
   * @param {number} daysForward - Days to look forward for upcoming launches (default: 7)
   */
  getDashboardData: async (userId, page = 1, pageSize = 12, daysBack = 7, daysForward = 7) => {
    try {
      const response = await api.get(ROUTES.API.INVESTOR.DASHBOARD, {
        params: {
          user_id: userId,
          page,
          page_size: pageSize,
          days_back: daysBack,
          days_forward: daysForward
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  },

  // ===== OPPORTUNITY EXPLORATION =====
  
  /**
 * Get available opportunities for exploration with optional filters
 * @param {Object} filters - Optional filters for the query
 */
  getOpportunities: async (filters = {}) => {
    try {
      // Map frontend filter names to API parameter names
      const apiParams = {};
      
      // Map deal size filters
      if (filters.dealSizeMin !== undefined) {
        apiParams.min_deal_size = filters.dealSizeMin;
      }
      
      if (filters.dealSizeMax !== undefined) {
        apiParams.max_deal_size = filters.dealSizeMax;
      }
      
      // Map return range filters
      if (filters.minReturnMin !== undefined) {
        apiParams.min_return = filters.minReturnMin;
      }
      
      // Map asset types filter - NEW PRIMARY FILTER
      if (filters.assetTypes && filters.assetTypes.length > 0) {
        apiParams.asset_type = filters.assetTypes.join(',');
        console.log('📡 Adding asset_type to API params:', apiParams.asset_type);
      }
      
      // Map strategy focus filter - NEW SECONDARY FILTER
      if (filters.strategyFocus && filters.strategyFocus.length > 0) {
        apiParams.strategy_focus = filters.strategyFocus.join(',');
        console.log('📡 Adding strategy_focus to API params:', apiParams.strategy_focus);
      }
      
      // Map instrument types filter
      if (filters.instrumentTypes && filters.instrumentTypes.length > 0) {
        apiParams.instrument_type = filters.instrumentTypes.join(',');
      }
      
      // Map instrument sub-types filter
      if (filters.instrumentSubTypes && filters.instrumentSubTypes.length > 0) {
        apiParams.instrument_sub_type = filters.instrumentSubTypes.join(',');
      }
      
      // Map sectors filter
      if (filters.sectors && filters.sectors.length > 0) {
        apiParams.sector = filters.sectors.join(',');
      }
      
      // Map locations filter - convert array to comma-separated string
      if (filters.locations && filters.locations.length > 0) {
        apiParams.location = filters.locations.join(',');
      }
      
      // Map dealTypes filter
      if (filters.dealTypes && filters.dealTypes.length > 0) {
        apiParams.deal_type = filters.dealTypes.join(',');
      }
      
      // Map days remaining to a timeline limit
      if (filters.daysRemainingMax !== undefined && filters.daysRemainingMax < 180) {
        apiParams.timeline_limit = filters.daysRemainingMax;
      }
      
      // Map search query if provided
      if (filters.search) {
        apiParams.search = filters.search;
        if (filters.searchType) {
          apiParams.search_type = filters.searchType;
        }
      }
      
      // Map any other filters
      if (filters.stage && filters.stage.length > 0) {
        apiParams.stage = filters.stage.join(',');
      }
      
      // Handle openOnly flag or statuses filter
      if (filters.openOnly === true) {
        apiParams.status = 'Open';
      } else if (filters.statuses && filters.statuses.length > 0) {
        apiParams.status = filters.statuses.join(',');
      }
  
      console.log('📡 [InvestorService] Final API params being sent:', apiParams);
      console.log('📡 [InvestorService] Original filters received:', filters);
      
      const response = await api.get(ROUTES.API.INVESTOR.OPPORTUNITIES, { 
        params: apiParams 
      });
      
      return {
        items: response.data.items || [],
        next_token: response.data.next_token,
        has_more: response.data.has_more,
        total_count: response.data.total_count,
        count: response.data.count
      };
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      throw error;
    }
  },

 /**
 * Get grid-optimized opportunities with pagination and metrics (UNIFIED ENDPOINT)
 * @param {Object} filters - Optional filters for the query
 * @param {number} page - Page number for pagination
 * @param {number} limit - Number of items per page
 */
getOpportunitiesGrid: async (filters = {}, page = 1, limit = 10, userInfo = null) => {
  try {
    // Map frontend filter names to API parameter names
    const apiParams = {
      page,
      limit
    };
    
    // Add institution_type and investor_id if available (for visibility filtering)
    console.log('🔍 [investorService] userInfo:', userInfo);
    if (userInfo?.institution_type) {
      apiParams.institution_type = userInfo.institution_type;
    }
    
    // Add investor_id for specific investor filtering
    if (userInfo?.user_id) {
      apiParams.investor_id = userInfo.user_id;
    }
    console.log('📡 [investorService] Sending apiParams:', apiParams);
    
    // Map deal size filters
    if (filters.dealSizeMin !== undefined) {
      apiParams.min_deal_size = filters.dealSizeMin;
    }
    
    if (filters.dealSizeMax !== undefined) {
      apiParams.max_deal_size = filters.dealSizeMax;
    }
    
    // Map return range filters
    if (filters.minReturnMin !== undefined) {
      apiParams.min_return = filters.minReturnMin;
    }
    
    // Map asset types filter - NEW PRIMARY FILTER
    if (filters.assetTypes && filters.assetTypes.length > 0) {
      apiParams.asset_type = filters.assetTypes.join(',');
    }
    
    // Map strategy focus filter - NEW SECONDARY FILTER
    if (filters.strategyFocus && filters.strategyFocus.length > 0) {
      apiParams.strategy_focus = filters.strategyFocus.join(',');
    }
    
    // Map instrument types filter
    if (filters.instrumentTypes && filters.instrumentTypes.length > 0) {
      apiParams.instrument_type = filters.instrumentTypes.join(',');
    }
    
    // Map instrument sub-types filter
    if (filters.instrumentSubTypes && filters.instrumentSubTypes.length > 0) {
      apiParams.instrument_sub_type = filters.instrumentSubTypes.join(',');
    }
    
    // Map sectors filter
    if (filters.sectors && filters.sectors.length > 0) {
      apiParams.sector = filters.sectors.join(',');
    }
    
    // Map regions filter
    if (filters.regions && filters.regions.length > 0) {
      apiParams.region = filters.regions.join(',');
    }
    
    // Map dealTypes filter
    if (filters.dealTypes && filters.dealTypes.length > 0) {
      apiParams.deal_type = filters.dealTypes.join(',');
    }
    
    // Map days remaining to a timeline limit (if applicable)
    if (filters.daysRemainingMax !== undefined) {
      apiParams.timeline_limit = filters.daysRemainingMax;
    }
    
    // Map search query if provided
    if (filters.search) {
      apiParams.search = filters.search;
      // Add searchType if present
      if (filters.searchType) {
        apiParams.search_type = filters.searchType;
      }
    }
    
    // Add any other filters that might be needed
    if (filters.stage && filters.stage.length > 0) {
      apiParams.stage = filters.stage.join(',');  // Convert array to comma-separated string
    }
    
    // Handle statuses - FIX: Change from status[] to status
    if (filters.statuses && filters.statuses.length > 0) {
      apiParams.status = filters.statuses.join(',');  // Convert array to comma-separated string
    }
    
    // Map initial_load flag for chart data
    if (filters.initial_load !== undefined) {
      apiParams.initial_load = filters.initial_load;
    }

    console.log('Grid API Params:', apiParams); // For debugging
    
    const response = await api.get(ROUTES.API.INVESTOR.OPPORTUNITIES_GRID, { 
      params: apiParams 
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching opportunities grid/metrics data:', error);
    throw error;
  }
},

  /**
   * Get detailed information about a specific opportunity
   * @param {string} opportunityId - ID of the opportunity to fetch
   */
  getOpportunityDetails: async (opportunityId) => {
    try {
      const response = await api.get(ROUTES.API.INVESTOR.FETCH_COINVEST_DETAILS, {
        params: { co_invest_id: opportunityId }
      });
      return response.data;
    } catch (error) {
      // Use structured logging to prevent format string injection vulnerabilities
      console.error('Error fetching opportunity details:', { opportunityId, error });
      throw error;
    }
  },

  /**
   * Get detailed information about a specific firm
   * @param {string} firmId - ID of the firm to fetch
   */
  getFirmDetails: async (firmId) => {
    try {
      const response = await api.get('/fetch/firms', {
        params: { firm_id: firmId }
      });
      return response.data.firm; // Extract firm object from response
    } catch (error) {
      // Use structured logging to prevent format string injection vulnerabilities
      console.error('Error fetching firm details:', { firmId, error });
      throw error;
    }
  },

  /**
   * Get calendar view data
   * @param {string} startDate - Start date for calendar range
   * @param {string} endDate - End date for calendar range
   * @param {Object} filters - Optional filters
   */
  getCalendarView: async (startDate, endDate, filters = {}) => {
    try {
      // Handle openOnly flag similarly to getOpportunities
      const apiFilters = { ...filters };
      
      if (apiFilters.openOnly === true) {
        apiFilters.excludeStatus = 'Closed to Investment';
      }
      
      // Remove openOnly from API params
      delete apiFilters.openOnly;
      
      // Handle statuses array - convert to comma-separated string
      if (apiFilters.statuses && apiFilters.statuses.length > 0) {
        apiFilters.status = apiFilters.statuses.join(',');
        delete apiFilters.statuses;
      }
      
      const response = await api.get(ROUTES.API.INVESTOR.CALENDAR_VIEW, {
        params: {
          start_date: startDate,
          end_date: endDate,
          ...apiFilters
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching calendar view:', error);
      throw error;
    }
  },

  /**
   * Get geographic view data
   * @param {Object} filters - Optional filters including region
   */
  getGeographicView: async (filters = {}) => {
    try {
      // Handle openOnly flag similarly to getOpportunities
      const apiFilters = { ...filters };
      
      if (apiFilters.openOnly === true) {
        apiFilters.excludeStatus = 'Closed to Investment';
      }
      
      // Remove openOnly from API params
      delete apiFilters.openOnly;
      
      const response = await api.get(ROUTES.API.INVESTOR.GEOGRAPHIC_VIEW, {
        params: apiFilters
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching geographic view:', error);
      throw error;
    }
  },

  /**
   * Get all trending opportunities (all categories in single request)
   * @param {string} timeframe - Timeframe for trending calculation (day, week, month)
   * @param {boolean} openOnly - Deprecated parameter (backend now queries open opportunities directly) - kept for API compatibility
   * @param {object} filters - Filter criteria to apply to trending data
   * @param {object} userPreferences - User preferences including notification_timing_days for dynamic day ranges
   */
  getAllTrendingOpportunities: async (timeframe = 'week', openOnly, filters = {}, userPreferences = {}) => {
    try {
      // Prepare request body with timeframe, filters, and user preferences
      const requestBody = {
        timeframe,
        filters: {
          sectors: filters.sectors || [],
          instrumentTypes: filters.instrumentTypes || [],
          instrumentSubTypes: filters.instrumentSubTypes || [],
          dealTypes: filters.dealTypes || [],
          dealSizeMin: filters.dealSizeMin,
          dealSizeMax: filters.dealSizeMax,
          minReturnMin: filters.minReturnMin,
          daysRemainingMax: filters.daysRemainingMax,
          locations: filters.locations || []
        },
        user_preferences: {
          notification_timing_days: userPreferences.notification_timing_days || 10
        }
      };
      
      // Use POST method with request body instead of GET with query parameters
      const response = await api.post(ROUTES.API.INVESTOR.TRENDING, requestBody);
      return response.data;
    } catch (error) {
      console.error('Error fetching all trending opportunities:', error);
      throw error;
    }
  },

  /**
   * Get trending opportunities (legacy method - kept for compatibility)
   * @deprecated Use getAllTrendingOpportunities instead for better performance
   */
  getTrendingOpportunities: async (category = 'popular', openOnly = true) => {
    console.warn('getTrendingOpportunities is deprecated. Use getAllTrendingOpportunities for better performance.');
    
    try {
      // Get all categories and return just the requested one
      const allData = await this.getAllTrendingOpportunities('week', openOnly);
      
      // Map category names to response keys
      const categoryMap = {
        'popular': 'popular',
        'fast-filling': 'fast_filling',
        'recent': 'recent', 
        'closing-soon': 'closing_soon'
      };
      
      const categoryKey = categoryMap[category] || 'popular';
      const categoryData = allData[categoryKey] || {};
      
      // Return in legacy format for backward compatibility
      return {
        items: categoryData.opportunities || [],
        chart_data: categoryData.chart_data || [],
        summary: categoryData[`${categoryKey}_summary`] || categoryData.engagement_summary || {},
        category: category,
        timeframe: allData.timeframe || 'week',
        total_count: (categoryData.opportunities || []).length,
        scoring_weights: allData.scoring_weights || {}
      };
    } catch (error) {
      console.error('Error fetching trending opportunities (legacy):', error);
      throw error;
    }
  },

  // ===== WATCHLIST =====

  /**
   * Get threads for the current user
   * @param {string} status - Thread status filter (all, unread, etc.)
   * @param {Object} userInfo - User information object
   */
  getThreads: async (status = 'all', userInfo = null) => {
    try {
      if (!userInfo || !userInfo.user_id) {
        throw new Error('User information is required');
      }

      const response = await api.get(ROUTES.API.INVESTOR.THREADS, {
        params: { 
          status,
          user_id: userInfo.user_id,
          user_type: userInfo.user_type,
          user_name: userInfo.user_name
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching investor threads:', error);
      throw error;
    }
  },
  
  /**
   * Get user's watchlist
   * @param {Object} userInfo - User information object containing user_id, user_type, and user_name
   */
  getWatchlist: async (userInfo = null) => {
    try {
      if (!userInfo || !userInfo.user_id) {
        throw new Error('User information is required');
      }

      const response = await api.get(ROUTES.API.INVESTOR.WATCHLIST, {
        params: { 
          user_id: userInfo.user_id,
          user_type: userInfo.user_type,
          user_name: userInfo.user_name
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching watchlist:', error);
      throw error;
    }
  },

  /**
   * Add an opportunity to user's watchlist
   * @param {string} opportunityId - ID of the opportunity to add
   * @param {Object} userInfo - User information object containing user_id, user_type, and user_name
   */
  addToWatchlist: async (opportunityId, userInfo = null) => {
    try {
      if (!userInfo || !userInfo.user_id) {
        throw new Error('User information is required');
      }

      const response = await api.post(ROUTES.API.INVESTOR.WATCHLIST, { 
        opportunityId,
        user_id: userInfo.user_id,
        user_type: userInfo.user_type,
        user_name: userInfo.user_name
      });
      return response.data;
    } catch (error) {
      console.error('Error adding to watchlist:', error);
      throw error;
    }
  },

  /**
   * Remove an opportunity from user's watchlist
   * @param {string} opportunityId - ID of the opportunity to remove
   * @param {Object} userInfo - User information object containing user_id, user_type, and user_name
   */
  removeFromWatchlist: async (opportunityId, userInfo = null) => {
    try {
      if (!userInfo || !userInfo.user_id) {
        throw new Error('User information is required');
      }

      // Ensure opportunityId is a string
      const stringId = String(opportunityId);
      console.log('Removing from watchlist - ID:', stringId, 'Type:', typeof stringId);
      
      const path = ROUTES.getWatchlistItemPath(stringId);
      console.log('DELETE request to path:', path);
      
      const response = await api.delete(path, {
        params: {
          user_id: userInfo.user_id,
          user_type: userInfo.user_type,
          user_name: userInfo.user_name
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      throw error;
    }
  },

  // ===== MESSAGING & ENGAGEMENT =====
  
  /**
   * Get messages in a thread
   * @param {string} threadId - ID of the thread
   */
  getMessages: async (threadId) => {
    try {
      const path = ROUTES.getThreadMessagesPath(threadId);
      const response = await api.get(path);
      return response.data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  },
  
  /**
   * Send a message in a thread
   * @param {string} threadId - ID of the thread
   * @param {Object} messageData - Message content
   */
  sendMessage: async (threadId, messageData) => {
    try {
      const path = ROUTES.getThreadMessagesPath(threadId);
      const response = await api.post(path, messageData);
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },
  
  /**
   * Create a new inquiry for an opportunity
   * @param {Object} inquiryData - Inquiry data including user_id and user_type
   */
  createInquiry: async (inquiryData) => {
    try {
      // Make sure user_id and user_type are in the body
      if (!inquiryData.user_id || !inquiryData.user_type) {
        throw new Error('User ID and user type are required for creating an inquiry');
      }

      const response = await api.post(ROUTES.API.INVESTOR.INQUIRIES, inquiryData);
      return response.data;
    } catch (error) {
      console.error('Error creating inquiry:', error);
      throw error;
    }
  },
  
  /**
   * Mark a thread as read
   * @param {string} threadId - ID of the thread
   */
  markThreadAsRead: async (threadId) => {
    try {
      const path = ROUTES.getThreadReadPath(threadId);
      const response = await api.put(path);
      return response.data;
    } catch (error) {
      console.error('Error marking thread as read:', error);
      throw error;
    }
  },

  /**
   * Track engagement (view, watchlist) for a co-investment
   * @param {Object} engagementData - Engagement tracking data
   */
  trackEngagement: async (engagementData) => {
    try {
      const response = await api.post(ROUTES.API.INVESTOR.ENGAGEMENT, engagementData);
      return response.data;
    } catch (error) {
      console.error('Error tracking engagement:', error);
      throw error;
    }
  },

  // ===== PREFERENCES & SETTINGS =====
  
  /**
   * Get investor preferences
   * @param {string} investorId - Investor user ID (required for API call)
   */
  getPreferences: async (investorId) => {
    try {
      if (!investorId) {
        throw new Error('Investor ID is required for fetching preferences');
      }
      
      const response = await api.get(ROUTES.API.INVESTOR.PREFERENCES, { 
        params: { investor_id: investorId } 
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching investor preferences:', error);
      throw error;
    }
  },

  /**
   * Update investor profile preferences
   * @param {Object} preferences - Comprehensive investor preferences to update
   * @param {string} investorId - Investor user ID (required for API call)
   */
  updatePreferences: async (preferences, investorId) => {
    try {
      if (!investorId) {
        throw new Error('Investor ID is required for updating preferences');
      }
      
      const payload = { 
        investor_id: investorId,
        investor_preference: preferences
      };
      const response = await api.post(ROUTES.API.INVESTOR.PREFERENCES, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  },

  /**
   * Set up alerts for opportunities
   * @param {Object} alertConfig - Alert configuration
   */
  setupOpportunityAlerts: async (alertConfig) => {
    try {
      const response = await api.post(ROUTES.API.INVESTOR.ALERTS, alertConfig);
      return response.data;
    } catch (error) {
      console.error('Error setting up alerts:', error);
      throw error;
    }
  },

  /**
   * Get platform-wide feature settings (read-only, no admin ID required)
   * Used by investors to check whether explore_v2 and other flags are enabled.
   */
  getPlatformSettings: async () => {
    try {
      const response = await api.get('/admin/platform_features');
      return response.data;
    } catch (error) {
      console.error('Error fetching platform settings:', error);
      throw error;
    }
  },

  /**
   * Search anonymised opportunity teasers (Explore V2)
   * @param {Object} filters - Structured filters (asset_types, regions, sectors, etc.)
   * @param {string} semantic_query - Optional natural-language search string
   * @param {number} page_size - Max results per request (Explore uses 1000; server caps at 1000)
   * @param {number} offset - For loading additional pages when total exceeds page_size
   */
  searchOpportunitiesV2: async ({ filters = {}, semantic_query = '', page_size = 20, offset = 0 } = {}) => {
    try {
      const response = await api.post(ROUTES.API.INVESTOR.OPPORTUNITIES_V2, {
        filters,
        semantic_query,
        page_size,
        offset
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching V2 opportunities:', error);
      throw error;
    }
  },

  /**
   * Parse a natural-language FilterGenie query for Explore V2.
   * POST /investors/opportunities/v2/ai-filter — may target the original parser (lookup-aware) or V2-only lambda.
   * Response `filters` are normalized to snake_case for `searchOpportunitiesV2` and Explore state.
   * @param {string} query - User's natural-language query
   * @param {Object} currentFilters - Currently applied filters (sent as context)
   * @returns {Promise<{ success?: boolean, filters: Object, summary?: string, semantic_query?: string }>}
   */
  parseAiFilterV2: async (query, currentFilters = {}) => {
    try {
      const response = await api.post(ROUTES.API.INVESTOR.AI_FILTER_V2, {
        query,
        current_filters: currentFilters
      });
      const data = response.data || {};
      if (data.filters && typeof data.filters === 'object') {
        data.filters = normalizeExploreGenieFilters(data.filters);
      }
      return data;
    } catch (error) {
      console.error('Error parsing AI filter V2:', error);
      throw error;
    }
  },

  /**
   * Submit an investor reveal request for a blind teaser opportunity
   * @param {string} opportunity_id - The co_invest_id of the opportunity
   * @param {string} message - Optional message to include with the request
   */
  submitRevealRequest: async ({
    opportunity_id,
    co_invest_teaser_alias = '',
    investor_message = '',
    investor_id = '',
    expected_amount_m,
    expected_timeline,
    inquiry_type,
  } = {}) => {
    try {
      const body = { opportunity_id, co_invest_teaser_alias, investor_message, investor_id };
      if (expected_amount_m !== undefined) body.expected_amount_m = expected_amount_m;
      if (expected_timeline !== undefined) body.expected_timeline = expected_timeline;
      if (inquiry_type) body.inquiry_type = inquiry_type;
      const response = await api.post(ROUTES.API.INVESTOR.REVEAL_REQUESTS, body);
      return response.data;
    } catch (error) {
      console.error('Error submitting reveal request:', error);
      throw error;
    }
  },

  /**
   * Get the current investor's own reveal requests.
   * @param {string} statusFilter - 'PENDING'|'ADMIN_REVIEW'|'REVEALED'|'DECLINED'|'WITHDRAWN'|'all'
   */
  getMyRevealRequests: async (statusFilter = 'all', investorId) => {
    try {
      const params = { status: statusFilter };
      if (investorId) params.investor_id = investorId;
      const response = await api.get(ROUTES.API.INVESTOR.REVEAL_REQUESTS, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching investor reveal requests:', error);
      throw error;
    }
  },

  /**
   * Withdraw a pending reveal request.
   * @param {string} requestId
   */
  withdrawRevealRequest: async (requestId) => {
    try {
      const response = await api.post(
        `${ROUTES.API.INVESTOR.REVEAL_REQUESTS}/${requestId}/withdraw`
      );
      return response.data;
    } catch (error) {
      console.error('Error withdrawing reveal request:', error);
      throw error;
    }
  },
};


export default investorService;